import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  isOnboardingResponseForProject,
  isProgressionDataLoading,
  progressionQueryOptions
} from '../../utils/progressionQueryUtils';
import { ArrowRight } from 'lucide-react';
import ManagementDashboardFilters from './ManagementDashboardFilters';
import MissionProgressionView from './MissionProgressionView';
import SalesProgressionView from './SalesProgressionView';
import SalesAccountabilityPanel from './SalesAccountabilityPanel';
import OperationsAccountabilityPanel from './OperationsAccountabilityPanel';
import OperationsProgressionView from './OperationsProgressionView';
import InventoryProgressionView from './InventoryProgressionView';
import { InventoryAccountabilityPanel } from './InventoryProgressionDummy';
import TaskAccountabilityPanel from './TaskAccountabilityPanel';
import { getAllUsersByProjectId, getVerticalHealthOverview } from '../../services/api/implementation/impl';
import {
  getManagementOnboardingStepNavigation,
  MANAGEMENT_ONBOARDING_STEP_ACTION_LABELS
} from '../../utils/projectStructurePriorities';
import {
  buildManagementVerticalTabParams,
  getManagementVerticalTab
} from './managementDashboardUrlParams';

const PUBLIC_URL = process.env.PUBLIC_URL || '';
const VERTICAL_CARD_ICONS = {
  management: `${PUBLIC_URL}/management_black_icon.png`,
  sales: `${PUBLIC_URL}/sales_black_icon.png`,
  operations: `${PUBLIC_URL}/operations_black_icon.png`,
  inventory: `${PUBLIC_URL}/inventory_black_icon.png`
};

const VERTICAL_FILTERS = [
  { key: 'dashboard', label: 'Dashboard' },
  { key: 'management', label: 'Management' },
  { key: 'sales', label: 'Sales' },
  { key: 'operations', label: 'Operations' },
  { key: 'inventory', label: 'Inventory' }
];

const VERTICAL_META = {
  management: { iconSrc: VERTICAL_CARD_ICONS.management, iconAlt: 'Management' },
  sales: { iconSrc: VERTICAL_CARD_ICONS.sales, iconAlt: 'Sales' },
  operations: { iconSrc: VERTICAL_CARD_ICONS.operations, iconAlt: 'Operations' },
  inventory: { iconSrc: VERTICAL_CARD_ICONS.inventory, iconAlt: 'Inventory' }
};

const statusBorderClass = {
  green: 'border-t-emerald-500',
  yellow: 'border-t-amber-400',
  red: 'border-t-red-500'
};

const statusBarClass = {
  green: 'bg-emerald-500',
  yellow: 'bg-amber-400',
  red: 'bg-red-400'
};

const overallBarClass = {
  green: 'bg-emerald-400',
  yellow: 'bg-amber-300',
  red: 'bg-red-300'
};

const formatCount = (n) => String(n).padStart(2, '0');

const percentToStatus = (percent) => {
  if (percent >= 70) return 'green';
  if (percent >= 40) return 'yellow';
  return 'red';
};

const formatCtaLabel = (label) => {
  if (!label) return null;
  const trimmed = String(label).trim();
  if (!trimmed) return null;
  return trimmed.startsWith('+') ? trimmed : `+ ${trimmed}`;
};

const INACTIVE_TASK_LABELS = new Set(['Setup complete', 'No active step']);

const mapVerticalToCard = (vertical, healthContext = {}) => {
  const percent = vertical.percent ?? vertical.progress?.percent ?? 0;
  const meta = VERTICAL_META[vertical.key] || VERTICAL_META.management;
  const total = vertical.progress?.total ?? 0;
  const completed = vertical.progress?.completed ?? 0;
  const pending = vertical.pending ?? Math.max(0, total - completed);
  const task =
    vertical.nextStep ||
    (percent >= 100 ? 'Setup complete' : 'No active step');
  const isActionableTask = task && !INACTIVE_TASK_LABELS.has(task);
  const stepId =
    vertical.currentStepId ??
    vertical.ctaStepId ??
    (vertical.key === 'management' ? healthContext.currentStepId : null);
  const rawCtaLabel =
    vertical.ctaLabel ??
    vertical.cta?.label ??
    vertical.actionLabel ??
    (stepId ? MANAGEMENT_ONBOARDING_STEP_ACTION_LABELS[stepId] : null) ??
    (isActionableTask ? task : null);

  return {
    key: vertical.key,
    name: vertical.label,
    task,
    percent,
    pending,
    status: percentToStatus(percent),
    iconSrc: meta.iconSrc,
    iconAlt: meta.iconAlt,
    ctaLabel: percent >= 100 ? null : formatCtaLabel(rawCtaLabel),
    ctaStepId: stepId,
    ctaPath: vertical.ctaPath ?? vertical.cta?.path ?? null
  };
};

const statusIconBgClass = {
  green: 'bg-emerald-50',
  yellow: 'bg-amber-50',
  red: 'bg-red-50'
};

const VerticalIconBadge = ({ src, alt, status }) => (
  <div
    className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${
      statusIconBgClass[status] ?? 'bg-[#E8F4F6]'
    }`}>
    <img src={src} alt={alt} className="h-5 w-5 object-contain" />
  </div>
);

const verticalTabClass = (active) =>
  `shrink-0 py-3 text-sm font-semibold transition-colors duration-200 relative ${
    active ? 'text-[#2A565E]' : 'text-gray-400 hover:text-gray-600'
  }`;

const VerticalHealthCard = ({ card, onOpenVertical, onPrimaryCta }) => (
  <div
    className={`flex h-full flex-col rounded-2xl border border-gray-200 border-t-[3px] ${statusBorderClass[card.status]} bg-white p-4 shadow-sm`}>
    <div className="flex items-start gap-3">
      <VerticalIconBadge src={card.iconSrc} alt={card.iconAlt} status={card.status} />
      <div className="min-w-0 flex-1">
        <p className="text-[15px] font-bold leading-tight text-[#111827]">{card.name}</p>
        <p className="mt-1 text-xs leading-snug text-gray-500 line-clamp-2">{card.task}</p>
      </div>
    </div>

    <div>
      <p className="mb-1 text-right text-lg font-bold leading-none text-[#111827]">{card.percent}%</p>
      <div className="h-1.5 w-full overflow-hidden rounded-full bg-gray-100">
        <div
          className={`h-full rounded-full transition-all ${statusBarClass[card.status]}`}
          style={{ width: `${card.percent}%` }}
        />
      </div>
    </div>

    <p className="mt-2 text-xs text-gray-500">
      Pending:{' '}
      <span className="font-semibold text-gray-700">{formatCount(card.pending)}</span>
    </p>

    <div className="mt-2 flex flex-col items-start gap-2">
      {card.ctaLabel ? (
        <button
          type="button"
          onClick={() => onPrimaryCta?.(card)}
          title={card.ctaLabel}
          className="w-fit max-w-full truncate rounded-full border border-[#2A565E] bg-white px-2.5 py-1 text-left text-[11px] font-semibold leading-tight text-[#2A565E] transition-colors hover:bg-[#2A565E]/5">
          {card.ctaLabel}
        </button>
      ) : (
        <span className="block h-[26px]" aria-hidden />
      )}

      <button
        type="button"
        onClick={() => onOpenVertical?.(card)}
        className="inline-flex items-center gap-1 text-left text-xs font-medium text-[#111827] underline underline-offset-2 transition-colors hover:text-[#2A565E]">
        Open {card.name.toLowerCase()}
        <ArrowRight className="h-3.5 w-3.5" aria-hidden />
      </button>
    </div>
  </div>
);

const VerticalProgressionLayout = ({ progression, accountability = null }) => (
  <div
    className={`flex flex-col items-stretch gap-5 ${accountability ? 'xl:flex-row' : ''}`}>
    <div className="min-w-0 flex-1">{progression}</div>
    {accountability ? <div className="w-full shrink-0 xl:w-[22rem]">{accountability}</div> : null}
  </div>
);

const FilterSelect = ({ label, value, onChange, options, disabled = false, placeholder }) => (
  <div className="w-full">
    <p className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-gray-500">{label}</p>
    <div className="relative w-full">
      <select
        value={value ?? ''}
        onChange={onChange}
        disabled={disabled}
        className="h-10 w-full appearance-none rounded-xl border border-gray-300 bg-white px-4 pr-10 text-sm font-semibold text-[#0f2a45] outline-none disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-400">
        {placeholder ? (
          <option value="" disabled>
            {placeholder}
          </option>
        ) : null}
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      <svg
        className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500"
        viewBox="0 0 20 20"
        fill="currentColor">
        <path
          fillRule="evenodd"
          d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z"
          clipRule="evenodd"
        />
      </svg>
    </div>
  </div>
);

const ProgressionTabLoading = ({ message }) => (
  <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
    <div className="py-12 text-center text-sm text-gray-500">{message}</div>
  </div>
);

const ManagementProgressionTab = ({ filters, onFiltersChange, onFiltersLoadingChange }) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeVertical = getManagementVerticalTab(searchParams);
  const isDashboardView = activeVertical === 'dashboard';
  const isOperationsView = activeVertical === 'operations';
  const isSalesView = activeVertical === 'sales';
  const [operationsUserId, setOperationsUserId] = useState('');
  const [salesManagerId, setSalesManagerId] = useState('');
  const isFilterReady = filters?.projectID !== null && filters?.projectID !== undefined;
  const hasProjectSelected = isFilterReady && String(filters.projectID) !== '';
  const resolvedProjectId = hasProjectSelected ? Number(filters.projectID) : null;

  const {
    data: healthOverviewResponse,
    isPending: isHealthOverviewPending,
    isFetching: isHealthOverviewFetching,
    isError: isHealthOverviewError
  } = useQuery({
    queryKey: ['verticalHealthOverview', resolvedProjectId],
    queryFn: () => getVerticalHealthOverview(resolvedProjectId),
    enabled: isDashboardView && hasProjectSelected,
    ...progressionQueryOptions
  });

  const isHealthOverviewLoading = isProgressionDataLoading({
    isPending: isHealthOverviewPending,
    isFetching: isHealthOverviewFetching,
    response: healthOverviewResponse,
    projectId: resolvedProjectId
  });

  const needsProjectUsers = (isOperationsView || isSalesView) && hasProjectSelected;

  const { data: projectUsersResponse, isLoading: isLoadingProjectUsers } = useQuery({
    queryKey: ['progressionProjectUsers', resolvedProjectId],
    queryFn: () => getAllUsersByProjectId(resolvedProjectId, 1, 500),
    enabled: needsProjectUsers,
    ...progressionQueryOptions
  });

  useEffect(() => {
    setOperationsUserId('');
    setSalesManagerId('');
  }, [resolvedProjectId]);

  const operationsManagerOptions = useMemo(() => {
    if (isLoadingProjectUsers) return [];

    return (projectUsersResponse?.data || [])
      .filter((user) => user.vertical === 'Operations' && user.level === 'Medium')
      .map((user) => ({
        value: String(user.id),
        label: user.name
      }));
  }, [projectUsersResponse, isLoadingProjectUsers]);

  const salesManagerOptions = useMemo(() => {
    if (isLoadingProjectUsers) return [];

    return (projectUsersResponse?.data || [])
      .filter((user) => user.vertical === 'Sales' && user.level === 'Medium')
      .map((user) => ({
        value: String(user.id),
        label: user.name
      }));
  }, [projectUsersResponse, isLoadingProjectUsers]);

  const isResolvingSalesManager =
    isSalesView &&
    hasProjectSelected &&
    (isLoadingProjectUsers || (salesManagerOptions.length > 0 && !salesManagerId));

  const isResolvingOperationsUser =
    isOperationsView &&
    hasProjectSelected &&
    (isLoadingProjectUsers || (operationsManagerOptions.length > 0 && !operationsUserId));

  useEffect(() => {
    if (!isOperationsView || !hasProjectSelected) {
      setOperationsUserId('');
      return;
    }

    if (isLoadingProjectUsers) return;

    if (!operationsManagerOptions.length) {
      setOperationsUserId('');
      return;
    }

    const isCurrentSelectionValid = operationsManagerOptions.some(
      (option) => option.value === operationsUserId
    );

    if (!isCurrentSelectionValid) {
      setOperationsUserId(operationsManagerOptions[0].value);
    }
  }, [
    isOperationsView,
    hasProjectSelected,
    isLoadingProjectUsers,
    operationsManagerOptions,
    operationsUserId
  ]);

  useEffect(() => {
    if (!isSalesView || !hasProjectSelected) {
      setSalesManagerId('');
      return;
    }

    if (isLoadingProjectUsers) return;

    if (!salesManagerOptions.length) {
      setSalesManagerId('');
      return;
    }

    const isCurrentSelectionValid = salesManagerOptions.some(
      (option) => option.value === salesManagerId
    );

    if (!isCurrentSelectionValid) {
      setSalesManagerId(salesManagerOptions[0].value);
    }
  }, [
    isSalesView,
    hasProjectSelected,
    isLoadingProjectUsers,
    salesManagerOptions,
    salesManagerId
  ]);

  const healthData = isOnboardingResponseForProject(healthOverviewResponse, resolvedProjectId)
    ? healthOverviewResponse?.data
    : null;
  const verticalCards = useMemo(
    () =>
      (healthData?.verticals || []).map((vertical) =>
        mapVerticalToCard(vertical, {
          currentStepId: healthData?.currentStepId
        })
      ),
    [healthData]
  );
  const overallProgress =
    healthData?.overallSetupPercent ?? healthData?.progress?.percent ?? 0;
  const overallStatus = percentToStatus(overallProgress);

  const handleVerticalChange = useCallback(
    (vertical) => {
      setSearchParams(buildManagementVerticalTabParams(searchParams, vertical), { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const handleOpenVertical = useCallback(
    (card) => {
      handleVerticalChange(card.key);
    },
    [handleVerticalChange]
  );

  const handlePrimaryCta = useCallback(
    (card) => {
      if (card.ctaPath) {
        navigate(card.ctaPath, {
          state: resolvedProjectId ? { projectID: resolvedProjectId } : undefined
        });
        return;
      }

      if (card.key === 'management' && card.ctaStepId) {
        const nav = getManagementOnboardingStepNavigation(resolvedProjectId, card.ctaStepId);
        if (nav) {
          if (nav.localStorageTab) {
            localStorage.setItem('secondaryTab', nav.localStorageTab);
          }
          navigate(nav.path, { state: nav.state });
          return;
        }
      }

      handleVerticalChange(card.key);
    },
    [handleVerticalChange, navigate, resolvedProjectId]
  );

  const renderVerticalContent = () => {
    switch (activeVertical) {
      case 'management':
        return (
          <VerticalProgressionLayout
            progression={
              <MissionProgressionView
                key={resolvedProjectId ?? 'builder'}
                projectID={filters?.projectID}
              />
            }
            accountability={
              <TaskAccountabilityPanel
                key={resolvedProjectId ?? 'all'}
                projectID={filters?.projectID}
              />
            }
          />
        );
      case 'sales':
        if (isResolvingSalesManager) {
          return (
            <ProgressionTabLoading message="Loading sales progression..." />
          );
        }
        return (
          <VerticalProgressionLayout
            progression={
              <SalesProgressionView
                key={resolvedProjectId}
                projectID={filters?.projectID}
                salesManagerId={salesManagerId}
              />
            }
            accountability={
              <SalesAccountabilityPanel
                key={resolvedProjectId}
                projectID={filters?.projectID}
                selectedSalesManagerId={salesManagerId}
              />
            }
          />
        );
      case 'operations':
        if (isResolvingOperationsUser) {
          return (
            <ProgressionTabLoading message="Loading operations progression..." />
          );
        }
        return (
          <VerticalProgressionLayout
            progression={
              <OperationsProgressionView
                key={resolvedProjectId}
                projectID={filters?.projectID}
                operationsUserId={operationsUserId}
              />
            }
            accountability={
              <OperationsAccountabilityPanel
                key={resolvedProjectId}
                projectID={filters?.projectID}
                selectedOperationsUserId={operationsUserId}
              />
            }
          />
        );
      case 'inventory':
        return (
          <VerticalProgressionLayout
            progression={
              <InventoryProgressionView
                key={resolvedProjectId}
                projectID={filters?.projectID}
              />
            }
            accountability={
              <InventoryAccountabilityPanel
                key={resolvedProjectId}
                projectID={filters?.projectID}
              />
            }
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="w-full pb-8">
      <div className="sticky top-[4.1rem] z-20 border-t border-b border-gray-200 bg-white shadow-sm">
        <div className="flex items-center gap-6 overflow-x-auto px-10">
          {VERTICAL_FILTERS.map((filter) => {
            const isActive = activeVertical === filter.key;

            return (
              <button
                key={filter.key}
                type="button"
                onClick={() => handleVerticalChange(filter.key)}
                className={verticalTabClass(isActive)}>
                {filter.label}
                {isActive ? (
                  <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-[#2A565E] transition-all duration-300" />
                ) : null}
              </button>
            );
          })}
        </div>
      </div>

      <div className="flex w-full flex-col gap-5 p-6">
        <div
          className={`flex w-full flex-col gap-4 sm:flex-row ${
            isOperationsView || isSalesView ? 'max-w-[560px]' : 'max-w-[280px]'
          }`}>
          <ManagementDashboardFilters
            onFiltersChange={onFiltersChange}
            onLoadingChange={onFiltersLoadingChange}
            initialProjectID={filters?.projectID}
          />
          {isOperationsView && (
            <FilterSelect
              label="Operations Manager"
              value={operationsUserId}
              onChange={(event) => setOperationsUserId(event.target.value)}
              disabled={!hasProjectSelected || isLoadingProjectUsers}
              placeholder={
                hasProjectSelected && !isLoadingProjectUsers && !operationsManagerOptions.length
                  ? 'No operations managers found'
                  : 'Select operations manager'
              }
              options={operationsManagerOptions}
            />
          )}
          {isSalesView && (
            <FilterSelect
              label="Sales Manager"
              value={salesManagerId}
              onChange={(event) => setSalesManagerId(event.target.value)}
              disabled={!hasProjectSelected || isLoadingProjectUsers}
              placeholder={
                hasProjectSelected && !isLoadingProjectUsers && !salesManagerOptions.length
                  ? 'No sales managers found'
                  : 'Select sales manager'
              }
              options={salesManagerOptions}
            />
          )}
        </div>

        {isDashboardView ? (
          <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
            <div className="mb-4 flex items-start justify-between gap-6">
              <div className="min-w-0">
                <h3 className="text-xl font-semibold text-[#1E4A53]">Vertical Health Overview</h3>
                <p className="mt-0.5 text-sm text-gray-500">
                  Track setup progress across departments for the selected project
                </p>
              </div>
              {hasProjectSelected && !isHealthOverviewLoading && !isHealthOverviewError && healthData && (
                <div className="shrink-0 text-right">
                  <p className="text-3xl font-bold leading-none text-[#0f2a45]">{overallProgress}%</p>
                  <p className="mt-1 text-[10px] font-semibold uppercase tracking-wider text-gray-400">
                    Overall Setup
                  </p>
                </div>
              )}
            </div>

            {!hasProjectSelected && (
              <div className="py-12 text-center text-sm text-gray-500">
                Select a project to view vertical health overview.
              </div>
            )}

            {hasProjectSelected && isHealthOverviewLoading && (
              <div className="py-12 text-center text-sm text-gray-500">
                Loading vertical health overview...
              </div>
            )}

            {hasProjectSelected && isHealthOverviewError && !healthData && (
              <div className="py-12 text-center text-sm text-rose-600">
                Unable to load vertical health overview. Please try again.
              </div>
            )}

            {hasProjectSelected &&
              !isHealthOverviewLoading &&
              !isHealthOverviewError &&
              healthData &&
              verticalCards.length > 0 && (
              <>
                <div className="mb-5 h-2 w-full overflow-hidden rounded-full bg-gray-100">
                  <div
                    className={`h-full rounded-full transition-all ${overallBarClass[overallStatus]}`}
                    style={{ width: `${overallProgress}%` }}
                  />
                </div>

                <div className="grid grid-cols-1 items-stretch gap-4 sm:grid-cols-2 xl:grid-cols-4">
                  {verticalCards.map((card) => (
                    <VerticalHealthCard
                      key={card.key}
                      card={card}
                      onOpenVertical={handleOpenVertical}
                      onPrimaryCta={handlePrimaryCta}
                    />
                  ))}
                </div>
              </>
            )}

            {hasProjectSelected &&
              !isHealthOverviewLoading &&
              !isHealthOverviewError &&
              healthData &&
              verticalCards.length === 0 && (
              <div className="py-12 text-center text-sm text-gray-500">
                No vertical health data available for this project yet.
              </div>
            )}
          </div>
        ) : (
          renderVerticalContent()
        )}
      </div>
    </div>
  );
};

export default ManagementProgressionTab;
